import * as React from 'react'
import cx from 'classnames'
import { animated } from 'react-spring'
import styles from './StickyViewHeader.sass'
import {
  AnimationProgressProvider,
  useAnimationProgress,
} from 'contexts/AnimationProgressContext'
import { createSpringLerp, clamp } from 'utils/math-util'
import {
  useFallbackNumberSpring,
  ScrollOffsetInterpolation,
} from 'hooks/spring-hooks'
import ScrollShadow from 'components/ScrollShadow'

interface StickyViewHeaderProps {
  scrollOffset?: ScrollOffsetInterpolation
  children?: React.ReactNode
  bordered?: boolean
  /**
   * If specified, must be a TabBar component.
   */
  tabBar?: React.ReactNode
  /**
   * If true, the header will not have top padding.
   */
  noTopPadding?: boolean
}

/**
 * How much to move the header up
 */
const DELTA = 49

export function StickyViewHeader({
  children,
  scrollOffset,
  tabBar,
  bordered,
  noTopPadding,
}: StickyViewHeaderProps) {
  scrollOffset = useFallbackNumberSpring(scrollOffset)
  const compactionAnimationProgress = scrollOffset.to((v) => clamp(v / DELTA))
  const shadowAnimationProgress = scrollOffset.to((v) =>
    // Makes the shadow animation start a bit later.
    clamp(v / DELTA - 0.75)
  )

  const lerp = createSpringLerp(compactionAnimationProgress)
  const rootStyle = !noTopPadding
    ? {
        transform: lerp(0, -49).to((v) => `translateY(${v}px)`),
      }
    : {
        transform: lerp(0, -16).to((v) => `translateY(${v}px)`),
      }
  const contentStyle = {
    transform: lerp(0, 16).to((v) => `translateY(${v}px)`),
  }
  return (
    <AnimationProgressProvider progress={compactionAnimationProgress}>
      <animated.div className={styles.root} style={rootStyle}>
        <div className={cx(styles.pad, noTopPadding && styles.noTopPadding)}>
          <animated.div className={styles.content} style={contentStyle}>
            {children}
          </animated.div>
        </div>
        {tabBar && <div className={styles.tabBar}>{tabBar}</div>}
        {(tabBar || bordered) && (
          <animated.div
            style={{
              opacity: shadowAnimationProgress.to((v) => 1 - v),
            }}
            className={styles.border}
          />
        )}
        <ScrollShadow opacity={shadowAnimationProgress} />
      </animated.div>
    </AnimationProgressProvider>
  )
}

export interface StickyViewHeaderMainProps {
  children?: React.ReactNode
}

export function StickyViewHeaderMain({ children }: StickyViewHeaderMainProps) {
  return <div className={styles.main}>{children}</div>
}

export interface StickyViewHeaderSpacerProps {
  children?: React.ReactNode
}

export function StickyViewHeaderSpacer({
  children,
}: StickyViewHeaderSpacerProps) {
  return <div className={styles.spacer}>{children}</div>
}

export interface StickyViewHeaderExtrasProps {
  children?: React.ReactNode
}

export function StickyViewHeaderExtras({
  children,
}: StickyViewHeaderExtrasProps) {
  const progress = useAnimationProgress()
  const lerp = createSpringLerp(progress)
  const translate = lerp(0, 8).to((v) => `translateY(${v}px)`)
  return (
    <animated.div style={{ transform: translate }} className={styles.extras}>
      {children}
    </animated.div>
  )
}
